import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

import { useAppSelector } from 'redux/store';
import { EventOverviewFieldsFragment } from 'api/graphql-types';

import { Button } from 'components/button';
import { FilterButton, IFilterItem } from 'components/filter-button/filter-button';
import { CardNews } from 'components/card';
import { EmptyState } from 'components/empty-state/empty-state';

import styles from './event-overview.module.scss';
import { NumberParam, useQueryParams } from 'use-query-params';
import { searchEvents } from 'api/algolia';
import { EVENTS_OVERVIEW_LIMIT } from './data';
import useEventsFilters from './data/use-events-filter';
import clsx from 'clsx';
import { EventAlgoliaFragment } from 'redux/stores/events';
import { PAGE_QUERY_KEY, THEME_QUERY_KEY, ThemeArrayParam, usePagination, useTheme } from 'utils/hooks/queries';

type FetchEvents = (page: number, themeIds: string[]) => Promise<void>;

type EventsFetchProps = {
	page: number;
	themeIds: string[];
};

export interface IEventOverviewProps {
	module: EventOverviewFieldsFragment;
}

export const EventOverview = (props: IEventOverviewProps): React.JSX.Element => {
	const router = useRouter();
	const { formatMessage } = useIntl();

	const [query, setQuery] = useQueryParams({
		[PAGE_QUERY_KEY]: NumberParam,
		[THEME_QUERY_KEY]: ThemeArrayParam
	});

	const overviewRef = useRef<HTMLDivElement>(null);
	const { events, eventsCount } = useAppSelector((state) => state.events);

	const [requests, setRequests] = useState<EventAlgoliaFragment[]>(events || []);
	const [requestIsLoading, setRequestIsLoading] = useState<boolean>(false);
	const [requestCount, setRequestCount] = useState<number>(eventsCount);

	const [initialQuery, setInitialQuery] = useState<typeof query | undefined>();
	const [hasFiltered, setHasFiltered] = useState<boolean>(false);

	const { currentPage, setCurrentPage, Pagination } = usePagination(EVENTS_OVERVIEW_LIMIT);
	const { selectedThemes, setSelectedThemesParam } = useTheme();

	const fetchEvents: FetchEvents = async (page, themeIds): Promise<void> => {
		setRequestIsLoading(true);

		try {
			const { results, total } = await searchEvents(
				'',
				{
					themesIds: themeIds
				},
				{
					sort: 'asc',
					page,
					limit: EVENTS_OVERVIEW_LIMIT,
					locale: router.locale
				}
			);

			setRequests(results as EventAlgoliaFragment[]);
			setRequestCount(total);
		} catch {
			setRequests([]);
			setRequestCount(0);
		}

		setRequestIsLoading(false);
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 140;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.scrollY - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handleDeleteFilter = (): void => {
		setQuery({
			thema: null,
			pagina: null
		});
		setTempSelectedThemes([]);
		handleFetchEvents(undefined, true);
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			setCurrentPage(page);
			handleFetchEvents({ page });
		}
	};

	const handleFetchEvents = async (data?: Partial<EventsFetchProps>, reset?: boolean): Promise<void> => {
		const selectedPage = reset ? 1 : data?.page || currentPage;

		const selectedThemeIds = reset ? [] : data?.themeIds || selectedThemes.map((theme) => String(theme.id));

		scrollToTop();
		setHasFiltered(data !== undefined);
		fetchEvents(selectedPage, selectedThemeIds);
	};

	const eventsFilters = useEventsFilters(props.module, (themeIds: string[]) =>
		handleFetchEvents({ page: 1, themeIds })
	);

	useEffect(() => {
		handleFetchEvents(undefined);
		setTempSelectedThemes(selectedThemes.map((theme) => ({ id: theme.id, label: theme.label })));
	}, [initialQuery]);

	useEffect(() => {
		if (!hasFiltered && !initialQuery && query[THEME_QUERY_KEY]) {
			setInitialQuery(query);
		}
	}, [query]);

	const [tempSelectedThemes, setTempSelectedThemes] = useState<IFilterItem[]>([]);

	const applyFilters = (): void => {
		setSelectedThemesParam(tempSelectedThemes.length > 0 ? tempSelectedThemes : null);
		handleFetchEvents({ page: 1, themeIds: tempSelectedThemes.map((theme) => String(theme.id)) });
	};

	return (
		<div className={styles.eventOverview} ref={overviewRef}>
			<div className={styles.wrapper}>
				<div className={styles.filterBar}>
					<div className={styles.filterContent}>
						<span className={styles.label}>{props.module.filterLabel}</span>
						<div className={styles.filterButtons}>
							<span className={styles.themeFilter}>
								<FilterButton
									title={formatMessage({ id: 'modules.newsOverview.themeFilter.title' })}
									items={eventsFilters[0].options as IFilterItem[]}
									onChange={applyFilters}
									onSelectedItemsChanged={setTempSelectedThemes}
									selectedItems={tempSelectedThemes}
								/>
							</span>
							{!!selectedThemes.length && (
								<span className={styles.removeFilters}>
									<Button
										title={formatMessage(
											{ id: 'modules.newsOverview.deleteFilters' },
											{ amount: tempSelectedThemes.length }
										)}
										variant="tertiary-dark"
										onClick={handleDeleteFilter}
									/>
								</span>
							)}
						</div>
					</div>

					<div className={styles.amountOfResults}>
						{formatMessage({ id: 'global.results' }, { results: requestCount })}
					</div>
				</div>
				<div
					className={clsx(styles.content, requestIsLoading && styles.resultsLoading)}
					style={{
						opacity: requestIsLoading ? '0.2' : 1,
						height: requests?.length === 9 ? '1350px' : '300px'
					}}
				>
					{requests?.length > 0 ? (
						requests?.map(({ header, from, until, location, url }, index) => (
							<div key={index}>
								<CardNews
									id={`events-overview-${index}`}
									bodyText=""
									excerpt={header?.excerpt || ''}
									from={from}
									until={until}
									location={location}
									slug={url || ''}
									title={header?.title || ''}
									cover={header.cover}
									key={index}
									type="evenementen"
									priority={index < 3}
								/>
							</div>
						))
					) : (
						<div className={styles.emptyState}>
							<EmptyState onClearFilters={handleDeleteFilter} isLoading={requestIsLoading} />
						</div>
					)}
				</div>
			</div>
			<div className={styles.pagination}>
				<Pagination totalCount={requestCount} onPageChange={handlePageChange} />
			</div>
		</div>
	);
};

export default EventOverview;
